import { useEffect, useState } from 'react';
import { useChat } from '../../context/ChatContext';
import { ChatSidebar } from '../../components/chat/ChatSidebar';
import { ChatWindow } from '../../components/chat/ChatWindow';
import { supabase } from '../../lib/supabase';
import { ChatUser } from '../../hooks/useRealtimeChat';
import { MessageCircle } from 'lucide-react';

export default function MessagesPage() {
    const { chats, activeChatId, setActiveChatId, loading } = useChat();
    const [currentUser, setCurrentUser] = useState<ChatUser | null>(null);
    const [filter, setFilter] = useState<'all' | 'inquiries'>('all');

    // Fetch current user
    useEffect(() => {
        const fetchUser = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('email')
                    .eq('id', session.user.id)
                    .single();

                setCurrentUser({
                    id: session.user.id,
                    name: profile?.email || session.user.email || 'User',
                    avatar_url: undefined
                });
            }
        };
        fetchUser();
    }, []);

    const activeChat = chats.find(c => c.id === activeChatId);

    const getOtherParticipantName = (chat: any) => {
        if (!currentUser) return 'User';
        const other = chat.participants?.find((p: any) => p.user_id !== currentUser.id);
        return other?.profile?.email || 'User';
    };

    if (loading || !currentUser) {
        return (
            <div className="min-h-screen pt-16 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-500">Loading messages...</p>
            </div>
        );
    }



    // Filter chats
    const filteredChats = chats.filter(chat => {
        if (filter === 'all') return true;

        // Check if chat has an inquiry attached (using new inquiry relation)
        // Or if we don't have that yet, check for specific "inquiry" context in messages? 
        // For now, assume 'inquiry' field exists on Chat type as we added it.
        // Also check if inquiry status is 'pending' or similar if desired.
        return chat.inquiry && (chat.inquiry as any).status !== 'closed';
    });

    // ... (rest of code) ...

    return (
        <div className="h-[calc(100vh-64px)] bg-gray-100 dark:bg-gray-900">
            <div className="h-full max-w-7xl mx-auto flex bg-white dark:bg-gray-800 shadow-sm overflow-hidden md:rounded-lg md:h-[calc(100vh-64px)]">

                {/* Sidebar - Hidden on mobile if chat is open */}
                <div className={`w-full md:w-80 border-r border-gray-200 dark:border-gray-700 flex flex-col ${activeChatId ? 'hidden md:flex' : 'flex'}`}>
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                            <MessageCircle className="text-rose-600" />
                            Messages
                        </h2>

                        {/* Filter Tabs */}
                        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                            <button
                                onClick={() => setFilter('all')}
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'all'
                                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setFilter('inquiries')}
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'inquiries'
                                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                Inquiries
                            </button>
                        </div>
                    </div>

                    <ChatSidebar
                        chats={filteredChats}
                        activeChatId={activeChatId}
                        onSelectChat={setActiveChatId}
                        currentUserId={currentUser.id}
                    />
                </div>

                {/* Main Chat Area */}
                <div className={`flex-1 flex flex-col ${!activeChatId ? 'hidden md:flex' : 'flex'}`}>
                    {activeChatId && activeChat ? (
                        <ChatWindow
                            chatId={activeChatId}
                            currentUser={currentUser}
                            otherParticipantName={getOtherParticipantName(activeChat)}
                            onClose={() => setActiveChatId(null)}
                            activeChat={activeChat}
                        />
                    ) : (
                        <div className="hidden md:flex flex-1 flex-col items-center justify-center text-gray-400 bg-gray-50 dark:bg-gray-900/50">
                            <div className="bg-white dark:bg-gray-800 p-8 rounded-full shadow-sm mb-4">
                                <MessageCircle className="h-16 w-16 text-primary-200" />
                            </div>
                            <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300">Your Messages</h3>
                            <p className="mt-2 text-sm">Select a conversation to start chatting</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
